import { Button } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconRefresh } from "@tabler/icons-react"
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, useState } from "react"

import { Table } from "~/components/table/Table"
import {
  FindIngestionsResponseDto,
  FindIngestionsResponseDtoStatus,
  useImportBlankProducts,
  useImportProducts,
} from "~/gen/api"
import { ErrorPreviewModal } from "~/pages/Admin/Ingestion/ProductImport/components/ErrorPreviewModal"
import { ProductImportModal } from "~/pages/Admin/Ingestion/ProductImport/components/ProductImportModal"
import { TraceabilityModal } from "~/pages/Admin/Ingestion/ProductImport/components/TraceabilityModal"
import { useIngestionListColumns } from "~/pages/Admin/Ingestion/ProductImport/ingestionsLists.columns"

export type TraceabilityLaunchList = {
  id: string
  owner: string
  product_count: number
  traceabilityLaunchedAt?: Date | null
  status: FindIngestionsResponseDtoStatus
  document: {
    id: string
    name: string
  }
}

type Props = {
  ingestions: FindIngestionsResponseDto[]
  refetch: () => void
}

export function IngestionList({ ingestions, refetch }: Props) {
  const [modalOpened, drawerActions] = useDisclosure(false)
  const [errorModalOpened, errorModalActions] = useDisclosure(false)
  const [importModalOpened, importModalActions] = useDisclosure(false)
  const [selectedIngestionId, setSelectedIngestionId] = useState<string>("")
  const [traceability, setTraceability] = useState<TraceabilityLaunchList>({
    id: "",
    owner: "",
    product_count: 0,
    status: "PROCESSED",
    document: {
      id: "",
      name: "",
    },
  })
  const [selectedIngestion, setSelectedIngestion] =
    useState<FindIngestionsResponseDto | null>(null)

  const importProductsMutationOptions = useMemo(
    () => ({
      mutation: {
        onSuccess() {
          importModalActions.close()
        },
      },
    }),
    [importModalActions]
  )

  const { mutate: importProducts, isLoading: isImportingProducts } =
    useImportProducts(importProductsMutationOptions)

  const { mutate: importBlankProducts, isLoading: isImportingBlankProducts } =
    useImportBlankProducts(importProductsMutationOptions)

  const handleDrawerOpen = (ingestion: FindIngestionsResponseDto) => {
    drawerActions.open()
    setTraceability({
      id: ingestion.id,
      owner: ingestion.createdBy?.email ?? "",
      product_count: ingestion.product_count,
      traceabilityLaunchedAt: ingestion.traceabilityLaunchedAt
        ? new Date(ingestion.traceabilityLaunchedAt)
        : null,
      status: ingestion.status,
      document: {
        id: ingestion.document.id,
        name: ingestion.document.originalFileName ?? "",
      },
    })
  }

  const [isSelectedIngestionBlank, setIsSelectedIngestionBlank] =
    useState<boolean>(false)

  const handleErrorModalOpen = (ingestionId: string) => {
    setSelectedIngestionId(ingestionId)
    errorModalActions.open()
  }

  const handleImportModalOpen = (
    ingestion: FindIngestionsResponseDto,
    isBlankIngestion: boolean
  ) => {
    setIsSelectedIngestionBlank(isBlankIngestion)
    setSelectedIngestion(ingestion)
    importModalActions.open()
  }

  const columns = useIngestionListColumns({
    handleDrawerOpen,
    handleErrorModalOpen,
    handleImportModalOpen,
  })

  const table = useReactTable({
    data: ingestions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: "createdAt",
          desc: true,
        },
      ],
    },
  })

  return (
    <>
      <Button onClick={() => refetch()}>
        <IconRefresh />
      </Button>
      <Table table={table} pagination={true} />
      <TraceabilityModal
        traceability={traceability}
        modalOpened={modalOpened}
        drawerActions={drawerActions}
      />
      <ErrorPreviewModal
        ingestionId={selectedIngestionId}
        opened={errorModalOpened}
        onClose={errorModalActions.close}
      />
      <ProductImportModal
        isBlankIngestion={isSelectedIngestionBlank}
        ingestionId={selectedIngestion?.id ?? ""}
        isImportingProducts={isImportingProducts}
        isImportingBlankProducts={isImportingBlankProducts}
        productsImport={importProducts}
        importBlankProducts={importBlankProducts}
        opened={importModalOpened}
        close={importModalActions.close}
        productsVerifiedCount={selectedIngestion?.product_count ?? 0}
        userEmail={selectedIngestion?.createdBy?.email ?? ""}
      />
    </>
  )
}
